import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import { Box } from "@mui/material";

import { Layout } from "Layout";

import { NEW_RECORD_SKELETON } from "appConfig";

import { EditModalHeader } from "components/EditModalHeader";
import { FrmFieldGrid } from "components/FrmFieldGrid";
import { ObjectEditDialogContent } from "components/ObjectEditDialogContent";
import { FrmSelectFromObjectsWithHoverHelp } from "components/formFields/FrmSelectFromObjectsWithHoverHelp";
import { FrmTextField } from "components/formFields/FrmTextField";
import { FrmTextFieldWithHoverHelp } from "components/formFields/FrmTextFieldWithHoverHelp";
import { FrmCheckboxWithHoverHelp } from "components/formFields/frm/FrmCheckboxWithHoverHelp";
import { FixedLoadingIndicator } from "components/utility/FixedLoadingIndicator";
import { FormErrors } from "components/utility/FormErrors";
import { Loading } from "components/utility/Loading";
import { VSpace } from "components/utility/VSpace";

import { i18n } from "services/i18nService";
import { getRecord } from "services/sosInventoryService/sosApi";

import { useErrors } from "hooks/useErrors";
import { useRecord } from "hooks/useRecord";
import { useRelated } from "hooks/useRelated";

import {
  editModalLoadingIndicatorOn,
  editModalLoadingIndicatorOff,
} from "globalState/loadingSlice";

export function SalesRep(props) {
  const { objectType, id, onClose } = props;

  const dispatch = useDispatch();

  const [record, setRecord, handleInputFieldChange] = useRecord();
  const { errors, setErrors, isInError } = useErrors();

  const loadingState = useSelector((state) => state.loading.editModal);

  const users = useRelated("user");

  useEffect(() => {
    async function _getRecord() {
      if (id) {
        dispatch(editModalLoadingIndicatorOn());
        const response = await getRecord(objectType, id);
        setRecord(response);
        dispatch(editModalLoadingIndicatorOff());
      } else {
        const emptyRecord = NEW_RECORD_SKELETON[objectType]();
        setRecord(emptyRecord);
      }
    }
    _getRecord();
  }, [id, dispatch, setRecord, objectType]);

  return (
    <>
      <EditModalHeader
        record={record}
        setRecord={setRecord}
        onSaveAndKeepEditing={setRecord}
        objectType={objectType}
        setErrors={setErrors}
        text={record?.name || ""}
        handleClose={onClose}
      />

      {loadingState && <Loading />}

      {record && (
        <ObjectEditDialogContent>
          <Layout
            pageTitle={
              id
                ? `${i18n(`objectType.${objectType}.Sentence`)} ${
                    record?.name || ""
                  }`
                : i18n(`objectType.${objectType}.New`)
            }
          >
            <Box flexGrow="1" position="relative" m={2} mb={0}>
              <FormErrors errors={errors} setErrors={setErrors} />
              {record ? (
                <Box>
                  <FrmFieldGrid gridAutoFlow="dense">
                    <div style={{ gridColumn: "1 / 2", maxWidth: "24rem" }}>
                      <FrmTextField
                        name="firstName"
                        value={record.firstName}
                        label={i18n("frmLabel.FirstName")}
                        onValueBlur={handleInputFieldChange}
                        error={isInError("firstName")}
                      />
                    </div>
                    <div style={{ gridColumn: "1 / 2", maxWidth: "24rem" }}>
                      <FrmTextField
                        name="lastName"
                        value={record.lastName}
                        label={i18n("frmLabel.LastName")}
                        onValueBlur={handleInputFieldChange}
                        error={isInError("lastName")}
                      />
                    </div>
                    <div style={{ gridColumn: "1 / 2", maxWidth: "10rem" }}>
                      <FrmTextFieldWithHoverHelp
                        number
                        name="commissionRate"
                        label={i18n("frmLabel.PercentCommission")}
                        onValueBlur={handleInputFieldChange}
                        value={record.commissionRate}
                        helpText={i18n("hoverHelp.PercentCommission")}
                      />
                    </div>
                    <div style={{ gridColumn: "1 / 2", maxWidth: "10rem" }}>
                      <FrmTextFieldWithHoverHelp
                        money
                        name="commissionAmount"
                        label={i18n("frmLabel.CommissionAmount")}
                        onValueBlur={handleInputFieldChange}
                        value={record.commissionAmount}
                        helpText={i18n("hoverHelp.CommissionAmount")}
                      />
                    </div>
                    <div style={{ gridColumn: "1 / 2", maxWidth: "24rem" }}>
                      <FrmSelectFromObjectsWithHoverHelp
                        selectProps={{
                          name: "user",
                          options: users,
                          label: i18n("frmLabel.User"),
                          onValueChange: handleInputFieldChange,
                          value: record.user,
                          minWidth: "24rem",
                        }}
                        helpText={i18n("hoverHelp.User")}
                      />
                    </div>
                    <div style={{ gridColumn: "1 / 2" }}>
                      <FrmCheckboxWithHoverHelp
                        name="restrictCustomers"
                        value={record.restrictCustomers}
                        onValueChange={handleInputFieldChange}
                        label={i18n("frmLabel.RestrictCustomers")}
                        helpText={i18n("hoverHelp.RestrictCustomers")}
                      />
                    </div>
                  </FrmFieldGrid>
                  <VSpace space={1} />
                  <FrmFieldGrid gridAutoFlow="dense"></FrmFieldGrid>
                </Box>
              ) : (
                !isInError("record") && (
                  <FixedLoadingIndicator
                    text={`${i18n("global.Loading")}...`}
                  />
                )
              )}
            </Box>
            <VSpace space={4} />
          </Layout>
        </ObjectEditDialogContent>
      )}
    </>
  );
}
